import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { z } from 'zod/v4'
import { ApiError } from '@/api/client'
import { login } from '@/api/resources/auth'
import { FormInput } from '@/components/forms/form-input'
import { Button } from '@/components/ui/button'
import { useServerErrors } from '@/hooks/use-server-errors'
import { useAuthStore } from '@/stores/auth-store'

const loginSchema = z.object({
  email: z.email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormValues = z.infer<typeof loginSchema>

export const Route = createFileRoute('/_auth/login')({
  head: () => ({ meta: [{ title: 'Login - MyJunea' }] }),
  component: LoginPage,
})

function LoginPage(): React.ReactElement {
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()
  const { serverErrors, handleMutationError, clearServerErrors } = useServerErrors()

  const mutation = useMutation({
    mutationFn: async (data: LoginFormValues) => login(data.email, data.password),
    onSuccess: (response) => {
      clearServerErrors()
      setAuth(response.access_token, response.user, response.expires_at)
      void navigate({ to: '/' })
    },
    onError: handleMutationError,
  })

  const form = useForm({
    defaultValues: { email: '', password: '' } as LoginFormValues,
    validators: {
      onSubmit: loginSchema,
    },
    onSubmit: ({ value }) => {
      mutation.mutate(value)
    },
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <h1 className="text-primary text-3xl leading-10 font-extrabold tracking-tight">Login</h1>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          clearServerErrors()
          mutation.reset()
          void form.handleSubmit()
        }}
        className="flex flex-col gap-6"
      >
        <div className="flex flex-col gap-2.5">
          <form.Field name="email">
            {(field) => (
              <FormInput
                field={field}
                label=""
                type="email"
                placeholder="Email address"
                autoComplete="email"
                serverErrors={serverErrors}
              />
            )}
          </form.Field>

          <form.Field name="password">
            {(field) => (
              <FormInput
                field={field}
                label=""
                type="password"
                placeholder="Password"
                autoComplete="current-password"
                serverErrors={serverErrors}
              />
            )}
          </form.Field>
        </div>

        {mutation.error instanceof ApiError && mutation.error.status !== 422 && (
          <p className="text-destructive text-sm">{mutation.error.body.message}</p>
        )}

        <Button
          type="submit"
          className="h-12 w-full rounded-full text-base font-semibold"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Logging in...' : 'Login'}
          {!mutation.isPending && <ArrowRight className="ml-1 size-5" />}
        </Button>
      </form>

      <Link to="/forgot-password" className="text-muted-foreground text-center text-sm underline">
        Forgot password?
      </Link>
    </div>
  )
}
